import React, { useEffect, useMemo } from 'react';
import InputField from '../../../components/form/input-field';
import SelectField from '../../../components/form/select-field';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import ApiRequest from '../../../libs/ApiCall';
import { CurrencyTypes, EmptyFormInput, RegulatorTypes, SmoothScroll, TransactionRolesTypes, TransactionTypes } from "../../../libs/helper-support";
import { ErrorFlash, SuccessFlash } from '../../../libs/flash-notification';
import DatePickerField from '../../../components/form/date-picker';
import TransactionType from '../../../types/transaction-type';
import { removeCommas } from '../../../libs/helper-support';

//FIXED SecurityTypes
const SecurityTypes = [
  { value: 'Commercial Paper', name: 'Commercial Paper', transactionType: 'Debt Capital Market' },
  { value: 'Public Bond', name: 'Public Bond', transactionType: 'Debt Capital Market' },
  { value: 'Private Bond', name: 'Private Bond', transactionType: 'Debt Capital Market' },

  { value: 'Rights Issue', name: 'Rights Issue', transactionType: 'Equity Capital Markets' },
  { value: 'Follow-on Offer', name: 'Follow-on Offer', transactionType: 'Equity Capital Markets' },
  { value: 'Initial Public Offer ("IPO")', name: 'Initial Public Offer ("IPO")', transactionType: 'Equity Capital Markets' },

  { value: 'Merger', name: 'Merger', transactionType: 'Financial Advisory' },
  { value: 'Acquisition', name: 'Acquisition', transactionType: 'Financial Advisory' },
  { value: 'Divestment', name: 'Divestment', transactionType: 'Financial Advisory' },
];

interface TransactionFormType {
  onSuccess?: (data: any) => void,
  onLoading?: (state: boolean) => void,
  setModalOpen: (state: boolean) => void,
  modalOpen?: boolean,
  transaction?: TransactionType,
  isEdit?: boolean,
}

const TransactionForm = ({ onLoading, onSuccess, setModalOpen, modalOpen, transaction, isEdit }: TransactionFormType) => {

  const formSchema = yup.object({
    issuer: yup.string().required(),
    type: yup.string().required('Transaction type is required'),
    security: yup.string().nullable(),
    regulatorQuasi: yup.string().nullable(),
    series: yup.string().nullable(),
    transactionSize: yup.string().required('Transaction size is required'),
    transactionSizeCurrency: yup.string().required('This field is required'),
    completionIssueDate: yup.string().nullable(),
    role: yup.string().nullable(),
  });

  const {
    setValue,
    setError,
    formState: { errors },
    handleSubmit,
    getValues,
    reset,
    watch
  } = useForm({ resolver: yupResolver(formSchema) });

  const selectedTransactionType = watch('type');

  //Filtered securities
  const filteredSecurityOptions = useMemo(() => {
    return SecurityTypes
      .filter(item => item.transactionType === selectedTransactionType)
      .map(({ transactionType, ...rest }) => rest);
  }, [selectedTransactionType]);

  //Default currency (FIXED)
  useEffect(() => {
    setValue('transactionSizeCurrency', 'NGN');
  }, []);

  //Reset form
  useEffect(() => {
    reset(EmptyFormInput(formSchema));
    transaction && reset(transaction);
  }, [modalOpen]);

  //Reset security when type changes
  useEffect(() => {
    setValue('security', '');
  }, [selectedTransactionType]);

  const onSubmit = async (data: any) => {
    onLoading && onLoading(true);
    data['transactionSize'] = removeCommas(data['transactionSize']);

    let request;
    if (isEdit) {
      request = await ApiRequest({ endpoint: `transaction/${data.uuid}/update`, method: 'POST', payload: data });
    } else {
      request = await ApiRequest({ endpoint: '/transaction/add-new', method: 'POST', payload: data });
    }

    onLoading && onLoading(false);

    if (request.status === '200') {
      onSuccess && onSuccess(request.data);
      setModalOpen(false);
      reset(EmptyFormInput(formSchema));
      SuccessFlash(isEdit ? 'Transaction updated successfully' : 'Transaction created successfully');
      return;
    }

    ErrorFlash(request.message);

    if (request.status === "payloadValidationError") {
      for (const [field, messages] of Object.entries(request.data)) {
        setError(field as any, { type: "manual", message: (messages as any).join(", ") });
        SmoothScroll();
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='grid mt-10 gap-y-5'>

      <InputField
        placeholder='Enter company name'
        label='Client/Issuer'
        error={errors.issuer?.message}
        onChangeInput={(e) => [setValue('issuer', e.target.value), setError('issuer', { message: '' })]}
        value={getValues('issuer')}
      />

      <SelectField
        label='Transaction Type'
        placeholder='Select'
        options={TransactionTypes}
        error={errors.type?.message}
        onChangeInput={(e) => [setValue('type', e), setError('type', { message: '' })]}
        value={getValues('type')}
      />

      <SelectField
        label='Security'
        placeholder='Select'
        options={filteredSecurityOptions}
        error={errors.security?.message}
        onChangeInput={(e) => [setValue('security', e), setError('security', { message: '' })]}
        value={getValues('security') ?? ''}
      />

      <SelectField
        label='Regulator/Quasi Regulator'
        placeholder='Select'
        options={RegulatorTypes}
        error={errors.regulatorQuasi?.message}
        onChangeInput={(e) => [setValue('regulatorQuasi', e), setError('regulatorQuasi', { message: '' })]}
        value={getValues('regulatorQuasi') ?? ''}
      />

      <InputField
        placeholder='Enter series'
        label='Series (if applicable)'
        error={errors.series?.message}
        onChangeInput={(e) => [setValue('series', e.target.value), setError('series', { message: '' })]}
        value={getValues('series') ?? ''}
      />

      <div className='flex w-full space-x-3'>
        <div className='w-4/5'>
          <InputField
            placeholder='Enter amount'
            type='money-format'
            label='Transaction Size'
            error={errors.transactionSize?.message}
            onChangeInput={(e) => [setValue('transactionSize', e.target.value), setError('transactionSize', { message: '' })]}
            value={getValues('transactionSize')}
          />
        </div>

        <div className='w-1/5'>
          <SelectField
            placeholder='Select'
            label='currency'
            labelClass="opacity-0"
            options={CurrencyTypes}
            error={errors.transactionSizeCurrency?.message}
            onChangeInput={(e) => [setValue('transactionSizeCurrency', e), setError('transactionSizeCurrency', { message: '' })]}
            value={getValues('transactionSizeCurrency') ?? 'NGN'}
          />
        </div>
      </div>

      <DatePickerField
        label='Transaction Completion Date/Issue Date'
        placeholder='Choose date'
        error={errors.completionIssueDate?.message}
        onChangeInput={(e) => [setValue('completionIssueDate', e), setError('completionIssueDate', { message: '' })]}
        value={getValues('completionIssueDate') ?? ''}
      />

      <SelectField
        label='Role'
        placeholder='Select'
        options={TransactionRolesTypes}
        error={errors.role?.message}
        onChangeInput={(e) => [setValue('role', e), setError('role', { message: '' })]}
        value={getValues('role') ?? ''}
      />

      <div className='mt-8'>
        <button className='pryBtn w-fit'>
          {isEdit ? 'Update' : 'Create'} Transaction
        </button>
      </div>
    </form>
  );
};

export default TransactionForm;